import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './PublicTeamHealthMetrics.module.css';
import { PublicPageLayout } from '../../components/PublicPageLayout/PublicPageLayout';
import { useLanguageFromUrl } from '../../hooks/useLanguageFromUrl';
import { useLanguageUrlSync } from '../../hooks/useLanguageUrlSync';

import { ActionPlanStepResolver } from './logic/ActionPlanStepResolver';
import { NavigationHelper } from './logic/NavigationHelper';
import { ErrorStateResolver } from './logic/ErrorStateResolver';

import { useActionPlanStep } from './hooks/useActionPlanStep';
import { useActionPlanFlow } from './hooks/useActionPlanFlow';
import { usePublicSurveyData } from './hooks/usePublicSurveyData';

import { ErrorContent } from './components/ErrorContent/ErrorContent';
import { TeamResults } from './components/TeamResults/TeamResults';
import { ResultsFooter } from './components/ResultsFooter/ResultsFooter';
import { LanguageToggle } from '../../components/LanguageToggle/LanguageToggle';
import { ActionPlanContent } from './components/ActionPlanContent/ActionPlanContent';

export const PublicTeamHealthMetrics = () => {
  const { guid } = useParams<{ guid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useLanguageFromUrl();
  const { changeLanguage } = useLanguageUrlSync();

  const isDirectActionPlanRoute = useMemo(
    () => NavigationHelper.isActionPlanRoute(location.pathname),
    [location.pathname],
  );

  const surveyData = usePublicSurveyData(guid);

  const { step: actionPlanStep, hasSubmitted } = useActionPlanStep({
    guid,
    isDirectActionPlanRoute,
  });

  const actionPlanFlow = useActionPlanFlow();

  const handleBackToResults = useCallback(() => {
    if (!guid) return;
    const baseUrl = NavigationHelper.getResultsUrl(guid);
    const url = i18n.language === 'en' ? `${baseUrl}?language=EN` : baseUrl;
    navigate(url);
  }, [guid, navigate, i18n.language]);

  const handleViewFullReport = useCallback(() => {
    if (!guid) return;
    const baseUrl = NavigationHelper.getActionPlanUrl(guid);
    const url = i18n.language === 'en' ? `${baseUrl}?language=EN` : baseUrl;
    navigate(url);
  }, [guid, navigate, i18n.language]);

  const errorState = useMemo(
    () =>
      ErrorStateResolver.checkForErrors({
        guid,
        isLoadingSurvey: surveyData.isLoadingSurvey,
        isLoadingMetrics: surveyData.isLoadingMetrics,
        surveyError: surveyData.surveyError,
        metricsError: surveyData.metricsError,
        survey: surveyData.survey,
        metricsLength: surveyData.metrics.length,
      }),
    [guid, surveyData],
  );

  const effectiveStep = useMemo(
    () =>
      ActionPlanStepResolver.resolveEffectiveStep(
        actionPlanStep,
        actionPlanFlow.currentStep,
      ),
    [actionPlanStep, actionPlanFlow.currentStep],
  );

  if (!guid) return null;

  if (errorState.shouldShowError) {
    return (
      <PublicPageLayout>
        <ErrorContent errorType={errorState.errorType} />
      </PublicPageLayout>
    );
  }

  if (actionPlanStep === 'none') {
    return (
      <PublicPageLayout
        fullBleed
        headerActions={<LanguageToggle onChange={changeLanguage} />}
        footer={<ResultsFooter />}
      >
        <TeamResults
          surveyTitle={surveyData.survey!.title}
          metrics={surveyData.metrics}
          hasSubmittedActionPlan={hasSubmitted}
          surveyUuid={guid}
          onViewFullReport={handleViewFullReport}
        />
      </PublicPageLayout>
    );
  }

  const actionPlanContent = (
    <ActionPlanContent
      step={effectiveStep}
      surveyUuid={guid}
      selectedCategoryId={actionPlanFlow.selectedCategoryId}
      submittedChoices={actionPlanFlow.submittedChoices}
      onFocusContinue={actionPlanFlow.handleFocusContinue}
      onSelectionSuccess={actionPlanFlow.handleSelectionSuccess}
      onSelectionBack={actionPlanFlow.handleSelectionBack}
      onBackToResults={handleBackToResults}
    />
  );

  // Oppsummeringen av handlingsplanen har sin egen layout og sitt eget språkvalg
  if (effectiveStep === 'aggregated') {
    return (
      <PublicPageLayout>
        <div className={classes.pageContainer}>{actionPlanContent}</div>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout fullBleed headerActions={<LanguageToggle onChange={changeLanguage} />}>
      {actionPlanContent}
    </PublicPageLayout>
  );
};
